// De inhoud van een notitie is een boom van blokken (het model van ProseMirror, en dus van
// de editor in de browser). De server bewaart die boom als JSON en leidt er twee dingen uit
// af: platte tekst voor de zoekindex en het fragment in de lijst, en markdown voor de
// export.
//
// Waarom de server dat doet en niet de browser: de zoekindex moet kloppen ook als een
// notitie via de API binnenkomt (delen-naar vanaf Android), en de export moet werken als er
// geen browser in de buurt is (de nachtelijke back-up).

export type Merk = { type: string; attrs?: Record<string, unknown> };
export type Blok = {
  type: string;
  attrs?: Record<string, unknown>;
  content?: Blok[];
  marks?: Merk[];
  text?: string;
};

export const LEEG: Blok = { type: "doc", content: [{ type: "paragraph" }] };

// --- tags en links ----------------------------------------------------------------------

// Een tag begint met #, daarna letters of cijfers, en mag met "/" genest worden
// (#klant/eijsink). Hij moet aan het begin van een woord staan, zodat een adres als
// https://iets#anders geen tag oplevert.
const TAG = /(?:^|[\s(])#([\p{L}\p{N}][\p{L}\p{N}_/-]*)/gu;

/** Alle tags die in deze tekst staan, klein geschreven en zonder dubbele. */
export function tagsUitTekst(tekst: string): string[] {
  const gevonden = new Set<string>();
  for (const treffer of tekst.matchAll(TAG)) {
    const naam = treffer[1].toLowerCase().replace(/\/+$/, "").slice(0, 60);
    // Alleen cijfers is geen tag maar een nummer (#2 in "kamer #2"), en een hekje aan het
    // eind van een woord ook niet.
    if (naam && !/^\d+$/.test(naam)) gevonden.add(naam);
  }
  return [...gevonden];
}

/**
 * Tags uit een notitie. Code blijft buiten schot: een `#include` of een kleurcode als
 * `#fff` in een codeblok is geen tag, en dat zou de tagbrowser vol rommel zetten.
 */
export function tagsUit(doc: Blok | null | undefined, titel = ""): string[] {
  const stukken: string[] = titel ? [titel] : [];
  const loop = (knoop: Blok) => {
    if (knoop.type === "codeBlock") return;
    if (knoop.type === "text") {
      if (knoop.marks?.some((m) => m.type === "code")) return;
      stukken.push(knoop.text ?? "");
      return;
    }
    for (const kind of knoop.content ?? []) loop(kind);
  };
  if (doc) loop(doc);
  return tagsUitTekst(stukken.join("\n"));
}

/**
 * Een tag hernoemen ín de tekst. Tags staan in de notitie zelf, dus hernoemen is de tekst
 * herschrijven — een rij in een tabel omzetten zou de tekst en de index uit elkaar laten
 * lopen. Geneste tags gaan mee: #klant hernoemen raakt ook #klant/eijsink.
 */
export function hernoemTagInDoc(doc: Blok | null | undefined, oud: string, nieuw: string): Blok | null {
  if (!doc) return null;
  const patroon = new RegExp(`(^|[\\s(])#${oud.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?=$|[\\s,.;:!?)/]|/)`, "giu");
  const loop = (knoop: Blok): Blok => {
    if (knoop.type === "codeBlock") return knoop;
    if (knoop.type === "text") {
      if (knoop.marks?.some((m) => m.type === "code")) return knoop;
      const nieuweTekst = (knoop.text ?? "").replace(patroon, (_heel, voor: string) => `${voor}#${nieuw}`);
      return nieuweTekst === knoop.text ? knoop : { ...knoop, text: nieuweTekst };
    }
    if (!knoop.content) return knoop;
    return { ...knoop, content: knoop.content.map(loop) };
  };
  return loop(doc);
}

export type NotitieLink = { id: string; titel: string };

/** Alle verwijzingen naar andere notities, in volgorde van voorkomen. */
export function linksUit(doc: Blok | null | undefined): NotitieLink[] {
  const gevonden = new Map<string, NotitieLink>();
  const loop = (knoop: Blok) => {
    if (knoop.type === "notitieLink") {
      const id = String(knoop.attrs?.id ?? "");
      if (id && !gevonden.has(id)) gevonden.set(id, { id, titel: String(knoop.attrs?.titel ?? "") });
    }
    for (const kind of knoop.content ?? []) loop(kind);
  };
  if (doc) loop(doc);
  return [...gevonden.values()];
}

/** Losse tekst uit een boom, met een regelovergang tussen blokken. Voedt FTS5. */
export function naarTekst(doc: Blok | null | undefined): string {
  if (!doc) return "";
  const regels: string[] = [];
  const loop = (knoop: Blok, regel: string[]) => {
    if (knoop.type === "text") {
      regel.push(knoop.text ?? "");
      return;
    }
    if (knoop.type === "notitieLink") {
      // De titel van de notitie waarnaar verwezen wordt, telt mee voor het zoeken: je zoekt
      // straks op "pinterminal" en vindt ook de notitie die ernaar verwijst.
      regel.push(String(knoop.attrs?.titel ?? ""));
      return;
    }
    if (knoop.type === "bijlage") {
      // En de naam van een bijlage ook: je zoekt op "offerte" en wilt de notitie waar die
      // pdf in hangt.
      regels.push(String(knoop.attrs?.naam ?? ""));
      return;
    }
    if (knoop.type === "hardBreak") {
      regel.push(" ");
      return;
    }
    const eigenRegel = BLOKKEN.has(knoop.type);
    const doel = eigenRegel ? [] : regel;
    for (const kind of knoop.content ?? []) loop(kind, doel);
    if (eigenRegel) {
      const tekst = doel.join("").trim();
      if (tekst) regels.push(tekst);
    }
  };
  loop(doc, []);
  return regels.join("\n");
}

const BLOKKEN = new Set([
  "paragraph",
  "heading",
  "codeBlock",
  "blockquote",
  "listItem",
  "taskItem",
  "tableRow"
]);

/** Korte samenvatting voor de notitielijst; de eerste regels zonder de titel. */
export function fragment(doc: Blok | null | undefined, lengte = 160): string {
  const tekst = naarTekst(doc).replace(/\s+/g, " ").trim();
  return tekst.length > lengte ? tekst.slice(0, lengte - 1).trimEnd() + "…" : tekst;
}

/** Eén regel uit een checklist, zoals hij in het takenoverzicht terechtkomt. */
export type TaakRegel = { nr: number; tekst: string; af: boolean; diepte: number };

/**
 * Alle checklist-regels uit een notitie, op volgorde van boven naar beneden.
 *
 * `nr` is die volgorde en verder niets: het is geen id dat bewaard blijft. Zet je er een taak
 * tussen, dan schuift alles eronder op — daarom controleert het afvinken vanuit het overzicht
 * ook of de tekst nog klopt voordat het een vinkje zet.
 *
 * Een subtaak is een eigen regel met een grotere `diepte`, en zijn tekst telt niet mee bij de
 * taak erboven: "boodschappen doen" met drie dingen eronder zijn vier taken, geen zin van
 * twintig woorden.
 */
export function takenUit(doc: Blok | null | undefined): TaakRegel[] {
  const uit: TaakRegel[] = [];
  const LIJSTEN = new Set(["taskList", "bulletList", "orderedList"]);

  const loop = (knoop: Blok, diepte: number) => {
    if (knoop.type === "taskItem") {
      const eigen = (knoop.content ?? []).filter((kind) => !LIJSTEN.has(kind.type));
      uit.push({
        nr: uit.length,
        tekst: eigen.map((kind) => naarTekst(kind)).join(" ").replace(/\s+/g, " ").trim(),
        af: knoop.attrs?.checked === true,
        diepte
      });
      for (const kind of knoop.content ?? []) loop(kind, diepte + 1);
      return;
    }
    for (const kind of knoop.content ?? []) loop(kind, diepte);
  };

  if (doc) loop(doc, 0);
  return uit;
}

/** Telt open en afgevinkte taken, voor de badge in de lijst ("2 van 5 af"). */
export function taken(doc: Blok | null | undefined): { af: number; totaal: number } {
  const regels = takenUit(doc);
  return { af: regels.filter((r) => r.af).length, totaal: regels.length };
}

// --- markdown ---------------------------------------------------------------------------

/** De soorten uitgelicht blok en hun markering in de export; zie web/src/onderdelen/Callout.ts. */
export const CALLOUT_MARKERING: Record<string, string> = { info: "NOTE", tip: "TIP", waarschuwing: "WARNING" };
export const CALLOUT_SOORT: Record<string, string> = { NOTE: "info", TIP: "tip", WARNING: "waarschuwing", IMPORTANT: "waarschuwing", CAUTION: "waarschuwing" };

function merkenOm(tekst: string, merken: Merk[] | undefined): string {
  if (!merken?.length) return tekst;
  let uit = tekst;
  // Code eerst: binnen `code` gelden de andere tekens niet als opmaak.
  if (merken.some((m) => m.type === "code")) uit = "`" + uit + "`";
  if (merken.some((m) => m.type === "bold")) uit = "**" + uit + "**";
  if (merken.some((m) => m.type === "italic")) uit = "*" + uit + "*";
  if (merken.some((m) => m.type === "strike")) uit = "~~" + uit + "~~";
  const link = merken.find((m) => m.type === "link");
  if (link) uit = `[${uit}](${adresNaarMarkdown(String(link.attrs?.href ?? ""))})`;
  return uit;
}

/**
 * Haakjes in een adres afschermen. Zonder dit levert een link als
 * `https://nl.wikipedia.org/wiki/Engels_(hoofdbetekenis)` een export op die bij het inlezen
 * halverwege afbreekt — en dan is de belofte "je kunt alles meenemen" stuk op precies het
 * soort adres dat je van een geknipte pagina meekrijgt.
 */
export function adresNaarMarkdown(adres: string): string {
  return adres.replace(/[\\()]/g, (teken) => "\\" + teken);
}

function regelTekst(knoop: Blok): string {
  if (knoop.type === "text") return merkenOm(knoop.text ?? "", knoop.marks);
  if (knoop.type === "notitieLink") {
    // In de export blijft de link leesbaar én terug te vinden: de titel voor een mens, het
    // id voor een import die de notities weer aan elkaar moet knopen.
    return `[${String(knoop.attrs?.titel ?? "")}](notitie:${String(knoop.attrs?.id ?? "")})`;
  }
  if (knoop.type === "hardBreak") return "  \n";
  if (knoop.type === "image") {
    const alt = String(knoop.attrs?.alt ?? "");
    const src = adresNaarMarkdown(String(knoop.attrs?.src ?? ""));
    return `![${alt}](${src})`;
  }
  // Een bijlage is in markdown gewoon een link: de naam voor een mens, het pad om hem terug
  // te vinden. Zo blijft de export bruikbaar buiten deze app.
  if (knoop.type === "bijlage") {
    return `[${String(knoop.attrs?.naam ?? "bijlage")}](${String(knoop.attrs?.url ?? "")})`;
  }
  return (knoop.content ?? []).map(regelTekst).join("");
}

function inspringen(tekst: string, met: string): string {
  return tekst
    .split("\n")
    .map((r) => (r ? met + r : r))
    .join("\n");
}

function blokNaarMarkdown(knoop: Blok, diepte: number): string {
  switch (knoop.type) {
    case "paragraph":
      return regelTekst(knoop);
    case "heading": {
      const niveau = Number(knoop.attrs?.level ?? 1);
      return "#".repeat(Math.min(Math.max(niveau, 1), 6)) + " " + regelTekst(knoop);
    }
    case "codeBlock": {
      const taal = String(knoop.attrs?.language ?? "");
      return "```" + taal + "\n" + (knoop.content ?? []).map((k) => k.text ?? "").join("") + "\n```";
    }
    case "blockquote":
      return inspringen(kinderenNaarMarkdown(knoop, diepte), "> ");
    case "callout": {
      // Als citaat met een markering erboven: `> [!NOTE]`. GitHub en steeds meer andere
      // lezers tonen dat als een gekleurd blok, en wie het niet kent leest gewoon een citaat.
      const markering = CALLOUT_MARKERING[String(knoop.attrs?.soort ?? "info")] ?? "NOTE";
      return inspringen(`[!${markering}]
` + kinderenNaarMarkdown(knoop, diepte), "> ");
    }
    case "horizontalRule":
      return "---";
    case "image":
    case "bijlage":
      return regelTekst(knoop);
    case "bulletList":
      return lijstNaarMarkdown(knoop, diepte, () => "- ");
    case "orderedList": {
      const start = Number(knoop.attrs?.start ?? 1);
      let nummer = start;
      return lijstNaarMarkdown(knoop, diepte, () => `${nummer++}. `);
    }
    case "taskList":
      return lijstNaarMarkdown(knoop, diepte, (kind) => (kind.attrs?.checked === true ? "- [x] " : "- [ ] "));
    case "table":
      return tabelNaarMarkdown(knoop);
    default:
      return kinderenNaarMarkdown(knoop, diepte);
  }
}

/**
 * Een tabel als pijptabel, zoals GitHub en elke markdown-editor hem leest. Een cel mag geen
 * regelovergang bevatten in deze vorm; die maken we tot een spatie, anders breekt de tabel.
 */
function tabelNaarMarkdown(tabel: Blok): string {
  const rijen = (tabel.content ?? []).filter((r) => r.type === "tableRow");
  if (rijen.length === 0) return "";

  const cellenVan = (rij: Blok): string[] =>
    (rij.content ?? []).map((cel) =>
      kinderenNaarMarkdown(cel, 1)
        .replace(/\s*\n\s*/g, " ")
        .replace(/\|/g, "\\|")
        .trim()
    );

  const kop = cellenVan(rijen[0]);
  const regels = [`| ${kop.join(" | ")} |`, `| ${kop.map(() => "---").join(" | ")} |`];
  for (const rij of rijen.slice(1)) regels.push(`| ${cellenVan(rij).join(" | ")} |`);
  return regels.join("\n");
}

function lijstNaarMarkdown(lijst: Blok, diepte: number, teken: (kind: Blok) => string): string {
  const regels: string[] = [];
  for (const kind of lijst.content ?? []) {
    const merk = teken(kind);
    const inhoud = kinderenNaarMarkdown(kind, diepte + 1);
    const [eerste, ...rest] = inhoud.split("\n");
    regels.push(merk + eerste);
    // Vervolgregels van hetzelfde item springen in met de breedte van het opsommingsteken,
    // anders leest een geneste lijst als een nieuwe lijst.
    for (const r of rest) regels.push(r ? " ".repeat(merk.length) + r : r);
  }
  return regels.join("\n");
}

function kinderenNaarMarkdown(knoop: Blok, diepte: number): string {
  const delen: string[] = [];
  for (const kind of knoop.content ?? []) {
    const tekst = blokNaarMarkdown(kind, diepte);
    if (tekst !== "") delen.push(tekst);
  }
  // Binnen een lijstitem geen lege regel tussen de blokken: dat maakt er losse alinea's van.
  return delen.join(diepte > 0 ? "\n" : "\n\n");
}

/** De inhoud van een notitie als markdown, zonder titel en zonder front matter. */
export function naarMarkdown(doc: Blok | null | undefined): string {
  if (!doc) return "";
  return kinderenNaarMarkdown(doc, 0).trim();
}
